import { Component, computed, inject, linkedSignal, signal } from "@angular/core"
import { NgClass } from "@angular/common"
import { httpResource } from "@angular/common/http"
import { ActivatedRoute, Router } from "@angular/router"
import { toSignal } from "@angular/core/rxjs-interop"
import { firstValueFrom, map } from "rxjs"
import { MatDialog } from "@angular/material/dialog"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { FormField, form, submit } from "@angular/forms/signals"
import {
    ModifyObjectFactory,
    ModifyPromotionalContent,
    ModifyPromotionalContentFactory,
    PROM_CONTENT_FIELD_LABELS,
    PromotionalContent,
} from "../../models"
import { PromotionalContentService, ToastService } from "../../services"
import { JsonPatchDocumentGenerator } from "../../helpers/JsonPatchDocumentGenerator"
import { BooleanInputComponent } from "../../components/boolean-input/boolean-input.component"
import { FileInputComponent } from "../../components/file-input/file-input.component"
import {
    ConfirmDialogComponent,
} from "../../components"
import {
    PATCH_IGNORED_FIELDS,
    describeError,
    invalidFieldsDetail,
    toServerFieldErrors,
} from "../../helpers/FormErrors"

@Component({
    selector: "valben-promotional-content-edit",
    templateUrl: "./promotional-content-edit.page.html",
    styles: [`
        :host {
            width: 100%;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [
        NgClass,
        MatProgressSpinnerModule,
        FormField,
        BooleanInputComponent,
        FileInputComponent,
    ],
})
export class PromotionalContentEditPage {
    private _promContentService = inject(PromotionalContentService)
    private _messageService = inject(ToastService)
    private _router = inject(Router)
    private _dialog = inject(MatDialog)
    private _patchGenerator = new JsonPatchDocumentGenerator()

    private _masterId = toSignal(inject(ActivatedRoute).paramMap.pipe(map(p => p.get("masterId") ?? "")), {
        initialValue: "",
    })

    promContent = httpResource<PromotionalContent>(() => {
        const masterId = this._masterId()
        return masterId ? this._promContentService.ByMasterIdUrl(masterId) : undefined
    })

    saving = signal(false)

    model = linkedSignal<ModifyPromotionalContent>(() => {
        const promContent = this.promContent.value()
        return promContent ? ModifyPromotionalContentFactory.from(promContent) : ModifyPromotionalContentFactory.create()
    })

    promContentForm = form(this.model)

    loading = computed(() => this.promContent.isLoading() || this.saving())
    loaded = computed(() => this.promContent.hasValue())

    async save(): Promise<void> {
        const original = this.promContent.value()
        if (!original) return

        this.saving.set(true)
        let failed = false

        const saved = await submit(this.promContentForm, {
            action: async f => {
                const patch = this._patchGenerator.getJsonPatchDocument(
                    ModifyPromotionalContentFactory.from(original),
                    f().value(),
                    PATCH_IGNORED_FIELDS
                )

                try {
                    await firstValueFrom(
                        this._promContentService.Modify(
                            this._masterId(),
                            ModifyObjectFactory.create(patch, original.version)
                        )
                    )
                    return null
                } catch (err) {
                    failed = true
                    const { detail, errors } = toServerFieldErrors(err, this.promContentForm, PROM_CONTENT_FIELD_LABELS)
                    this._error(detail)
                    return errors
                }
            },
            onInvalid: () => this._error(invalidFieldsDetail(this.promContentForm, PROM_CONTENT_FIELD_LABELS)),
        })

        this.saving.set(false)
        if (failed || !saved) return

        this._messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Uspješno izmijenjeno",
            life: 10000,
        })
        this.back()
    }

    async delete(): Promise<void> {
        this.saving.set(true)
        try {
            await firstValueFrom(this._promContentService.Delete(this._masterId()))
            this._messageService.add({
                severity: "success",
                summary: "Success",
                detail: "Uspješno obrisano",
                life: 10000,
            })
            this.back()
        } catch (err) {
            this._error(describeError(err).message)
        } finally {
            this.saving.set(false)
        }
    }

    async showDeleteModal(): Promise<void> {
        const confirmed = await firstValueFrom(
            this._dialog
                .open(ConfirmDialogComponent, {
                    width: "35rem",
                    data: { header: "Jeste li sigurni da želite obrisati entitet?" },
                })
                .afterClosed()
        )
        if (confirmed) await this.delete()
    }

    back(): void {
        this._router.navigate(["/promotional-content"])
    }

    private _error(detail: string): void {
        this._messageService.add({ severity: "error", summary: "Error", detail, life: 10000 })
    }
}
